import type { ProductCard, IngredientCard } from "./types.ts";
import { setAnalyticsSession } from "./analytics.ts";

declare const __DEFAULT_BACKEND__: string;

/**
 * Backend client. Single source of truth for the URL — set at build time
 * via Vite's `define`, overridable at runtime via `window.AMP_BACKEND_URL`
 * in case we want a single bundle to serve dev + prod.
 */
export function getBackend(): string {
  if (typeof window !== "undefined") {
    const w = window as unknown as { AMP_BACKEND_URL?: string };
    if (w.AMP_BACKEND_URL) return w.AMP_BACKEND_URL;
  }
  return __DEFAULT_BACKEND__;
}

const SESSION_STORAGE_KEY = "amp_chat_session_token";

/**
 * Get-or-create an anonymous session token. Persisted in localStorage so
 * the conversation continues across page loads and tabs (within the 7-day
 * server-side TTL).
 */
export async function getSessionToken(): Promise<string> {
  try {
    const cached = localStorage.getItem(SESSION_STORAGE_KEY);
    if (cached) {
      // Cache hit — still tell analytics so events fired before any chat
      // interaction get the right session attribution.
      setAnalyticsSession(cached);
      return cached;
    }
  } catch {
    // localStorage may be blocked (private browsing, certain embed contexts)
    // — fall through to fetching a fresh token each load.
  }

  const resp = await fetch(`${getBackend()}/session`);
  if (!resp.ok) throw new Error(`/session ${resp.status}`);
  const json = (await resp.json()) as { session_token: string };
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, json.session_token);
  } catch {
    // ignore storage failure
  }
  // Hand the token to analytics so subsequent track() calls can attribute
  // events to this session.
  setAnalyticsSession(json.session_token);
  return json.session_token;
}

export interface StreamEventHandlers {
  onText: (text: string) => void;
  onToolStarted: (info: { tool: string; query?: string }) => void;
  onToolFinished: (info: { tool: string; count?: number; found?: boolean }) => void;
  onProductCard: (card: ProductCard) => void;
  onIngredientCard: (card: IngredientCard) => void;
  onDone: (info: { usage: unknown; cards: number }) => void;
  onError: (message: string) => void;
}

/**
 * POST /chat with stream:true. The backend streams SSE events; we parse and
 * dispatch to the handlers. Returns when the stream closes.
 *
 * Cancellation: caller can pass an AbortSignal. The fetch + reader chain
 * stops when the signal aborts.
 */
export async function streamChat(
  args: {
    sessionToken: string;
    message: string;
    /**
     * Optional Shopify product handle. When set, the backend prepends
     * a context cue so the agent answers in the context of that product
     * (no "which product?" round-trip). Set this on PDPs.
     */
    productHandle?: string;
  },
  handlers: StreamEventHandlers,
  signal?: AbortSignal
): Promise<void> {
  const resp = await fetch(`${getBackend()}/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify({
      session_token: args.sessionToken,
      message: args.message,
      product_handle: args.productHandle,
      stream: true,
    }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => "");
    handlers.onError(`HTTP ${resp.status}${text ? `: ${text.slice(0, 200)}` : ""}`);
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE: events are blank-line separated.
    let idx;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const ev = parseSseEvent(raw);
      if (ev) dispatchEvent(ev, handlers);
    }
  }
}

function parseSseEvent(raw: string): { event: string; data: string } | null {
  const lines = raw.split("\n");
  let eventType = "message";
  let data = "";
  for (const line of lines) {
    if (line.startsWith("event:")) eventType = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!data) return null;
  return { event: eventType, data };
}

function dispatchEvent(
  ev: { event: string; data: string },
  handlers: StreamEventHandlers
): void {
  try {
    switch (ev.event) {
      case "text":
        handlers.onText(JSON.parse(ev.data) as string);
        break;
      case "tool_started":
        handlers.onToolStarted(JSON.parse(ev.data));
        break;
      case "tool_finished":
        handlers.onToolFinished(JSON.parse(ev.data));
        break;
      case "product_card":
        handlers.onProductCard(JSON.parse(ev.data) as ProductCard);
        break;
      case "ingredient_card":
        handlers.onIngredientCard(JSON.parse(ev.data) as IngredientCard);
        break;
      case "done":
        handlers.onDone(JSON.parse(ev.data));
        break;
      case "error": {
        const e = JSON.parse(ev.data) as { message?: string };
        handlers.onError(e.message ?? "stream error");
        break;
      }
    }
  } catch (err) {
    handlers.onError(`parse error in '${ev.event}' event: ${err instanceof Error ? err.message : "unknown"}`);
  }
}

export function clearStoredSession(): void {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}
